<script setup>
import { NFlex } from 'naive-ui'; 
import { computed } from 'vue';
import RadarChart from './RadarChart.vue'; 

import iconStudent from '@/assets/images/result/ai/icon_student.png';
import iconClass from '@/assets/images/result/ai/icon_class.png'; 

import icon01 from '@/assets/images/result/ai/icon_01.svg';
import icon02 from '@/assets/images/result/ai/icon_02.svg';
import icon03 from '@/assets/images/result/ai/icon_03.svg';
import icon04 from '@/assets/images/result/ai/icon_04.svg';

const props = defineProps({
  ourClassPercentages: { 
    type: Array,
    default: () => []
  },
  overallPercentages: { 
    type: Array,
    default: () => []
  }, 
  userRole: {
    type: String,
    default: 'teacher' 
  },
  itemLabels: {
    type: Array,
    default: () => ['AI·디지털 기술', 'AI·디지털 활용', 'AI·디지털 윤리', '인간 중심 AI 설계']
  }
});

// 역할 확인
const isTeacher = computed(() => props.userRole === 'teacher');

const classInfo = computed(() => {
  return {
    label: isTeacher.value ? '우리 학급 정답 비율' : '나의 정답 비율',
    icon: isTeacher.value ? iconClass : iconStudent
  };
});

// 전체 평균 
const totalLabel = 'AI·디지털 리터러시';
const overallTotal = computed(() => {
  if (!props.overallPercentages.length) return '0.00';
  const sum = props.overallPercentages.reduce((a, b) => a + b, 0);
  return (sum / props.overallPercentages.length).toFixed(2);
});

const formattedOverallRates = computed(() => {
  const icons = [icon01, icon02, icon03, icon04]; 

  return props.itemLabels.map((label, index) => {
    const rawValue = props.overallPercentages[index];  
    const percent = (rawValue !== undefined && typeof rawValue === 'number') 
      ? rawValue.toFixed(1) 
      : 'N/A';
    return {
      label: label,
      percent: percent,
      icon: icons[index] || icons[0] 
    };
  });
});
</script>

<template>
  <div class="card answer">     
      <h5 class="card-title">영역별 정답 비율 비교</h5>    
      <n-flex :wrap="false" :size="20" class="pt-2">
        <div class="chart-wrap" style="width: 550px"> 
          <div class="inner">
            <p class="label">{{ itemLabels[0] }}</p> 
            <n-flex :wrap="false" :size="0">
              <p class="label">{{ itemLabels[3] }}</p> 
              <RadarChart 
                :item-labels="itemLabels"
                :our-class-percentages="overallPercentages"
              />
              <p class="label">{{ itemLabels[1] }}</p> 
            </n-flex>
            <p class="label">{{ itemLabels[2] }}</p> 
          </div>
        </div>

        <div class="box">
          <n-flex justify="center" align="center" :size="8">
            <img :src="classInfo.icon" alt="icon">
            <span v-html="classInfo.label" class="font-bold"></span>
          </n-flex>   
          <ul class="rates pt-3">
            <li v-for="(item, index) in formattedOverallRates" :key="index">
              <img :src="item.icon" :alt="item.label" class="rate-icon">
              <span class="rate-label">{{ item.label }}</span>
              <span class="rate-percent">{{ item.percent }}%</span>
            </li>
          </ul>
          <ul class="helper text-[1rem] flex flex-col pt-3">
            <li>이 그래프는 하위 영역 간 상대적 수행 능력 차이를 의미하지 않습니다.</li>
          </ul>
        </div>
      </n-flex>   
  </div>
</template>

<style lang="scss" scoped>
.box { 
  background-color: var(--result-ai-primary-50);
  border-radius: 10px;
  flex: 1;
  padding: 1rem 1.5rem 1.125rem;
  .n-flex > img { height: 2rem; width: auto; }
  .rates {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;   
    li {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      background-color: var(--smoe-white); 
      padding: 0.75rem;
      border-radius: 0.5rem;      
      .rate-icon {
        width: 48px; 
        height: 48px;
        margin-bottom: 0.25rem;
      }
      .rate-label {
        font-size: 1rem;
        font-weight: 600;
        color: var(--result-ai-primary-800);
      }
      .rate-percent {
        font-size: 1.125rem;
        font-weight: 700;
      }
    }
  }
  .helper {
    li {
      position: relative; padding-left: 24px; text-align: justify;
      &::before {
        content: '';
        position: absolute;
        top: 8px;
        left: 10px;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: var(--result-ai-primary-500);
      }
    }
  }        
}
.chart-wrap {
  border: 1px solid var(--result-ai-primary-100); border-radius: 10px; padding: 20px;
  display: flex; align-items: center; justify-content: center;
  .inner { 
    display: flex; flex-direction: column; align-items: center; font-weight: 600; position: relative; 
    .label {
      font-size: 1rem;     
      color: var(--result-ai-primary-800);
    }
  }
  .n-flex {
    gap: 0; align-items: center; text-align: center;    
  }
}
</style>